import { useAuth0 } from '@auth0/auth0-react'

import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { UserDTO } from '../models/UserDTO.model'
import UserService from '../services/UserService/UserService'
import { checkIsUpTree, scrollToBrowseMainSection } from '../utils'
import { LoginButton } from './LoginButton'
import { LogoutButton } from './LogoutButton'
import { RegisterButton } from './RegisterButton'
import ScrollToTop from './scrollToTop'
import { UserAvatar } from './UserAvatar'

const hospitalsBrowseLink = {
  hash: '',
  pathname: '/hospitals/' as const,
  state: { scrollToBrowse: true },
}
const agenciesBrowseLink = {
  hash: '#tab=agencies',
  pathname: '/hospitals' as const,
  state: { scrollToBrowse: true },
}

/** Run after menu close / route paint so #browse-main scroll isn’t overwritten (esp. signed-in mobile menu). */
const scheduleScrollToBrowseMain = () => {
  window.setTimeout(() => scrollToBrowseMainSection(), 160)
}

type MobileBrowseSectionsProps = {
  onNavigate: () => void
  handleHospitalsClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  handleAgenciesClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

/** Shared Reviews + Tools groups for the mobile slide-down menu */
const MobileBrowseSections = ({ onNavigate, handleHospitalsClick, handleAgenciesClick }: MobileBrowseSectionsProps) => (
  <>
    <li className="px-2 pb-1">
      <p className="px-5 pt-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Reviews</p>
      <ul className="space-y-0.5">
        <li className="border-b border-gray-100">
          <Link
            to={hospitalsBrowseLink}
            onClick={(e) => {
              handleHospitalsClick(e)
              onNavigate()
              scheduleScrollToBrowseMain()
            }}
            className="block"
          >
            <span className="flex py-3.5 px-5 min-h-[48px] items-center rounded-lg text-sm font-medium text-graphite-grey hover:bg-gray-50 active:bg-gray-100 transition-colors">
              Hospitals
            </span>
          </Link>
        </li>
        <li>
          <Link
            to={agenciesBrowseLink}
            onClick={(e) => {
              handleAgenciesClick(e)
              onNavigate()
              scheduleScrollToBrowseMain()
            }}
            className="block"
          >
            <span className="flex py-3.5 px-5 min-h-[48px] items-center rounded-lg text-sm font-medium text-graphite-grey hover:bg-gray-50 active:bg-gray-100 transition-colors">
              Agencies
            </span>
          </Link>
        </li>
      </ul>
    </li>
    <li className="px-2 py-2 border-t border-gray-100">
      <p className="px-5 pt-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Tools</p>
      <ul className="space-y-0.5">
        <li className="border-b border-gray-100">
          <Link to="/income-calculator/" onClick={onNavigate} className="block">
            <span className="flex py-3.5 px-5 min-h-[48px] items-center rounded-lg text-sm font-medium text-graphite-grey hover:bg-gray-50 active:bg-gray-100 transition-colors">
              Income calculator
            </span>
          </Link>
        </li>
        <li>
          <Link to="/gsa-calculator/" onClick={onNavigate} className="block">
            <span className="flex py-3.5 px-5 min-h-[48px] items-center rounded-lg text-sm font-medium text-graphite-grey hover:bg-gray-50 active:bg-gray-100 transition-colors">
              GSA calculator
            </span>
          </Link>
        </li>
      </ul>
    </li>
  </>
)

export const TopNav = (): JSX.Element => {
  const { user, isAuthenticated, isLoading } = useAuth0()
  const [userDetails, setUserDetails] = useState<UserDTO>()
  const [mobileMenuIsOpen, setMobileMenuOpen] = useState(false)
  const [reviewsDropdownOpen, setReviewsDropdownOpen] = useState(false)
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [profileSubmenuOpen, setProfileSubmenuOpen] = useState(false)
  const location = useLocation()
  const history = useHistory()

  const mobileMenuContainerRef = useRef<HTMLUListElement>(null)
  const mobileNavRef = useRef<HTMLDivElement>(null)
  const reviewsDropdownRef = useRef<HTMLDivElement>(null)
  const toolsDropdownRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const handleHospitalsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname !== '/hospitals' && location.pathname !== '/hospitals/') return
    e.preventDefault()
    history.push({
      hash: '',
      pathname: '/hospitals/',
      state: { scrollToBrowse: true },
    })
    setTimeout(() => {
      window.dispatchEvent(new HashChangeEvent('hashchange'))
    }, 100)
  }

  const handleAgenciesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/hospitals' || location.pathname === '/hospitals/') {
      e.preventDefault()
      history.push({
        hash: '#tab=agencies',
        pathname: '/hospitals',
        state: { scrollToBrowse: true },
      })
      setTimeout(() => {
        window.dispatchEvent(new HashChangeEvent('hashchange'))
      }, 100)
    }
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node

      // Check if click is inside any dropdown - if so, don't close
      if (
        (mobileNavRef.current && checkIsUpTree(target, mobileNavRef.current)) ||
        (reviewsDropdownRef.current && checkIsUpTree(target, reviewsDropdownRef.current)) ||
        (toolsDropdownRef.current && checkIsUpTree(target, toolsDropdownRef.current)) ||
        (userMenuRef.current && checkIsUpTree(target, userMenuRef.current))
      ) {
        return
      }

      // Close dropdowns if click is outside
      setMobileMenuOpen(false)
      if (reviewsDropdownRef.current) {
        setReviewsDropdownOpen(false)
      }
      if (toolsDropdownRef.current) {
        setToolsDropdownOpen(false)
      }
      if (userMenuRef.current) {
        setUserMenuOpen(false)
        setProfileSubmenuOpen(false)
      }
    }

    // Use click event (not capture phase) to allow links to handle their clicks first
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && user) {
      setUserDetails(UserService.formatUser(user))
    } else {
      setUserDetails(undefined)
    }
  }, [isAuthenticated, user])

  // Re-read the profile cache whenever another part of the app saves profile data
  // (e.g. OnboardingModal) without triggering a JWT refresh.
  useEffect(() => {
    if (!user) return
    const handler = () => setUserDetails(UserService.formatUser(user))
    window.addEventListener('scrubstr_profile_updated', handler)
    return () => window.removeEventListener('scrubstr_profile_updated', handler)
  }, [user])

  return (
    <div className="flex shrink-0 align-middle">
      {!isLoading && (
        <div className="relative z-[110] self-center">
          <ScrollToTop />
          {/* Mobile: guests get Log in + Sign up + ⋮ (nav); signed-in users get avatar menu */}
          <nav className="lg:hidden relative flex justify-end" aria-label="Mobile">
            <div ref={mobileNavRef} className="relative flex items-center gap-1.5 sm:gap-2">
              {isAuthenticated && userDetails ? (
                <button
                  type="button"
                  aria-expanded={mobileMenuIsOpen}
                  aria-haspopup="true"
                  aria-label="Account and menu"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMobileMenuOpen((open) => !open)
                  }}
                  className={`flex items-center gap-1.5 rounded-full p-1 pr-2 transition-all touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo focus-visible:ring-offset-2 ${
                    mobileMenuIsOpen
                      ? 'bg-gray-100 ring-2 ring-royal-indigo/30 ring-offset-2 ring-offset-white'
                      : 'bg-gray-50/80 ring-1 ring-gray-200/90 hover:ring-gray-300'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <UserAvatar className="transition-opacity hover:opacity-90" size="sm" userDetails={userDetails} />
                  <svg
                    className={`mr-0.5 h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${
                      mobileMenuIsOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              ) : (
                <>
                  <LoginButton className="shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-xs font-semibold text-graphite-grey transition-colors hover:bg-gray-50 sm:px-3 sm:text-sm">
                    Log in
                  </LoginButton>
                  <RegisterButton className="shrink-0 whitespace-nowrap rounded-lg bg-orange px-2.5 py-2 text-xs font-semibold text-white transition-opacity hover:bg-opacity-90 sm:px-3 sm:text-sm">
                    Sign up
                  </RegisterButton>
                  <button
                    type="button"
                    aria-expanded={mobileMenuIsOpen}
                    aria-haspopup="true"
                    aria-label="More navigation"
                    onClick={(e) => {
                      e.stopPropagation()
                      setMobileMenuOpen((open) => !open)
                    }}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-all touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-royal-indigo focus-visible:ring-offset-2 ${
                      mobileMenuIsOpen
                        ? 'border-royal-indigo bg-royal-indigo/5 text-royal-indigo'
                        : 'border-gray-200 bg-white text-graphite-grey shadow-sm hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <circle cx="12" cy="6" r="1.65" />
                      <circle cx="12" cy="12" r="1.65" />
                      <circle cx="12" cy="18" r="1.65" />
                    </svg>
                  </button>
                </>
              )}

              {mobileMenuIsOpen && (
                <ul
                  ref={mobileMenuContainerRef}
                  className="absolute right-0 top-full z-[110] mt-3 max-h-[min(78vh,560px)] w-[min(100vw-1.5rem,340px)] max-w-[calc(100vw-1.5rem)] overflow-x-hidden overflow-y-auto overscroll-contain rounded-2xl border border-gray-100 bg-white py-2 shadow-xl ring-1 ring-black/5"
                >
                  {isAuthenticated && userDetails ? (
                    <>
                      <li className="px-4 pb-3 pt-1 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            className="!h-12 !w-12 shrink-0 text-sm shadow-sm"
                            size="lg"
                            userDetails={userDetails}
                          />
                          <div className="min-w-0 flex-1 text-left">
                            <p className="truncate text-sm font-semibold text-graphite-grey">
                              {userDetails.name || 'Account'}
                            </p>
                            {userDetails.email ? (
                              <p className="truncate text-xs text-gray-500">{userDetails.email}</p>
                            ) : null}
                          </div>
                        </div>
                        <Link
                          to="/profile"
                          className="mt-3 flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-medium text-royal-indigo transition-colors hover:bg-gray-50"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          View profile
                        </Link>
                        {(!userDetails.work_experience || !userDetails.work_settings) && (
                          <Link
                            to="/profile"
                            className="mt-2 flex items-start gap-2 rounded-xl bg-yellow-50 border border-yellow-200 px-3 py-2.5 hover:bg-yellow-100 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span className="text-yellow-500 flex-shrink-0 mt-0.5">⚠</span>
                            <span className="text-xs text-yellow-800 font-medium leading-snug">
                              Complete your profile to boost review credibility
                            </span>
                          </Link>
                        )}
                      </li>
                      <MobileBrowseSections
                        onNavigate={() => setMobileMenuOpen(false)}
                        handleHospitalsClick={handleHospitalsClick}
                        handleAgenciesClick={handleAgenciesClick}
                      />
                      <li className="border-t border-gray-100 px-2 pt-1">
                        <button
                          type="button"
                          className="flex min-h-[48px] w-full items-center justify-between rounded-lg px-5 py-3.5 text-sm font-medium text-graphite-grey transition-colors hover:bg-gray-50"
                          onClick={() => setProfileSubmenuOpen((open) => !open)}
                        >
                          Profile
                          <svg
                            className={`h-4 w-4 transition-transform ${profileSubmenuOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {profileSubmenuOpen && (
                          <ul className="mb-1 ml-2 border-l border-gray-200 pl-2">
                            <li>
                              <Link
                                to="/my-reviews"
                                className="flex min-h-[44px] items-center rounded-lg px-4 py-2.5 text-sm text-graphite-grey hover:bg-gray-50"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                My Reviews
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/my-pages"
                                className="flex min-h-[44px] items-center rounded-lg px-4 py-2.5 text-sm text-graphite-grey hover:bg-gray-50"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                My Pages
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/profile"
                                className="flex min-h-[44px] items-center rounded-lg px-4 py-2.5 text-sm text-graphite-grey hover:bg-gray-50"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                Account settings
                              </Link>
                            </li>
                          </ul>
                        )}
                      </li>
                      <li className="px-2 pb-1" onClick={() => setMobileMenuOpen(false)}>
                        <LogoutButton className="flex min-h-[48px] w-full items-center rounded-lg px-5 py-3.5 text-left text-sm font-medium text-red-700 transition-colors hover:bg-red-50">
                          Log out
                        </LogoutButton>
                      </li>
                    </>
                  ) : (
                    <MobileBrowseSections
                      onNavigate={() => setMobileMenuOpen(false)}
                      handleHospitalsClick={handleHospitalsClick}
                      handleAgenciesClick={handleAgenciesClick}
                    />
                  )}
                </ul>
              )}
            </div>
          </nav>
          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center gap-6">
            {/* Reviews Dropdown */}
            <div className="relative" ref={reviewsDropdownRef}>
              <button
                onClick={() => {
                  setReviewsDropdownOpen(!reviewsDropdownOpen)
                  setToolsDropdownOpen(false)
                  setUserMenuOpen(false)
                }}
                onBlur={(evt) => {
                  if (!checkIsUpTree(evt.relatedTarget, reviewsDropdownRef.current)) {
                    setReviewsDropdownOpen(false)
                  }
                }}
                className="flex items-center gap-1 text-graphite-grey hover:text-royal-indigo transition-colors"
              >
                <NavItem>Reviews</NavItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transition-transform ${reviewsDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {reviewsDropdownOpen && (
                <div className="absolute left-0 top-full z-[120] mt-1 min-w-[200px] max-w-[min(100vw-2rem,320px)] rounded-b-lg border-t-2 border-royal-indigo bg-white shadow-lg ring-1 ring-black/5">
                  <Link
                    to={hospitalsBrowseLink}
                    className="block px-4 py-3 text-graphite-grey hover:bg-warm-sand transition-colors"
                    onClick={(e) => {
                      handleHospitalsClick(e)
                      setReviewsDropdownOpen(false)
                      scheduleScrollToBrowseMain()
                    }}
                  >
                    <NavItem>Hospitals</NavItem>
                  </Link>
                  <Link
                    to={agenciesBrowseLink}
                    className="block px-4 py-3 text-graphite-grey hover:bg-warm-sand transition-colors border-t border-gray-200"
                    onClick={(e) => {
                      handleAgenciesClick(e)
                      setReviewsDropdownOpen(false)
                      scheduleScrollToBrowseMain()
                    }}
                  >
                    <NavItem>Agencies</NavItem>
                  </Link>
                </div>
              )}
            </div>

            {/* Tools Dropdown */}
            <div className="relative" ref={toolsDropdownRef}>
              <button
                onClick={() => {
                  setToolsDropdownOpen(!toolsDropdownOpen)
                  setReviewsDropdownOpen(false)
                  setUserMenuOpen(false)
                }}
                onBlur={(evt) => {
                  if (!checkIsUpTree(evt.relatedTarget, toolsDropdownRef.current)) {
                    setToolsDropdownOpen(false)
                  }
                }}
                className="flex items-center gap-1 text-graphite-grey hover:text-royal-indigo transition-colors"
              >
                <NavItem>Tools</NavItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transition-transform ${toolsDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {toolsDropdownOpen && (
                <div className="absolute right-0 top-full z-[120] mt-1 min-w-[200px] max-w-[min(100vw-2rem,320px)] rounded-b-lg border-t-2 border-royal-indigo bg-white shadow-lg ring-1 ring-black/5">
                  <Link
                    to="/income-calculator"
                    className="block px-4 py-3 text-graphite-grey hover:bg-warm-sand transition-colors"
                    onClick={() => setToolsDropdownOpen(false)}
                  >
                    <NavItem>Income Calculator</NavItem>
                  </Link>
                  <Link
                    to="/gsa-calculator"
                    className="block px-4 py-3 text-graphite-grey hover:bg-warm-sand transition-colors border-t border-gray-200"
                    onClick={() => setToolsDropdownOpen(false)}
                  >
                    <NavItem>GSA Calculator</NavItem>
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              {isAuthenticated && userDetails ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setUserMenuOpen(!userMenuOpen)
                      setReviewsDropdownOpen(false)
                      setToolsDropdownOpen(false)
                    }}
                    className="flex items-center focus:outline-none touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <UserAvatar className="transition-opacity hover:opacity-90" size="md" userDetails={userDetails} />
                  </button>
                  {userMenuOpen && (
                    <div
                      className="absolute top-full right-0 mt-1 bg-white shadow-lg border-t-2 border-royal-indigo rounded-b-lg min-w-[220px] z-[9999]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(!userDetails.work_experience || !userDetails.work_settings) && (
                        <Link
                          to="/profile"
                          className="flex items-start gap-2 px-4 py-3 bg-yellow-50 border-b border-yellow-200 hover:bg-yellow-100 transition-colors cursor-pointer"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <span className="text-yellow-500 mt-0.5 flex-shrink-0">⚠</span>
                          <span className="text-xs text-yellow-800 font-medium leading-snug">
                            Complete your profile to boost review credibility
                          </span>
                        </Link>
                      )}
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-4 py-3 text-graphite-grey hover:bg-warm-sand transition-colors cursor-pointer"
                        onClick={() => setProfileSubmenuOpen((open) => !open)}
                      >
                        <NavItem>Profile</NavItem>
                        <svg
                          className={`h-4 w-4 transition-transform ${profileSubmenuOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {profileSubmenuOpen && (
                        <>
                          <Link
                            to="/my-reviews"
                            className="block px-6 py-3 text-graphite-grey hover:bg-warm-sand transition-colors border-t border-gray-200 cursor-pointer"
                            onClick={() => {
                              setUserMenuOpen(false)
                              setProfileSubmenuOpen(false)
                            }}
                          >
                            <NavItem>My Reviews</NavItem>
                          </Link>
                          <Link
                            to="/my-pages"
                            className="block px-6 py-3 text-graphite-grey hover:bg-warm-sand transition-colors border-t border-gray-200 cursor-pointer"
                            onClick={() => {
                              setUserMenuOpen(false)
                              setProfileSubmenuOpen(false)
                            }}
                          >
                            <NavItem>My Pages</NavItem>
                          </Link>
                          <Link
                            to="/profile"
                            className="block px-6 py-3 text-graphite-grey hover:bg-warm-sand transition-colors border-t border-gray-200 cursor-pointer"
                            onClick={() => {
                              setUserMenuOpen(false)
                              setProfileSubmenuOpen(false)
                            }}
                          >
                            <NavItem>Account settings</NavItem>
                          </Link>
                        </>
                      )}
                      <div className="border-t border-gray-200">
                        <LogoutButton
                          className="w-full text-left block px-4 py-3 text-graphite-grey hover:bg-warm-sand transition-colors cursor-pointer"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <NavItem>Logout</NavItem>
                        </LogoutButton>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <LoginButton className="border border-gray-300 bg-white text-graphite-grey px-6 py-2 rounded-md hover:bg-gray-50 transition-all">
                    <span className="font-medium">Log in</span>
                  </LoginButton>
                  <RegisterButton className="bg-orange text-white px-6 py-2 rounded-md hover:bg-opacity-90 transition-all">
                    <span className="font-medium">Sign up</span>
                  </RegisterButton>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </div>
  )
}

// NavItem component containing inner span used as label for the nav item
interface NavItemProps extends React.HTMLAttributes<HTMLBaseElement> {
  children?: ReactNode
}
export const NavItem = (props: NavItemProps): JSX.Element => {
  const { children, className, ...otherProps } = props

  return (
    <span {...otherProps} className={`font-open font-550 text-14 uppercase tracking-widest leading-3em ` + className}>
      {children}
    </span>
  )
}
